import React, { useEffect, useState } from 'react';
import { DollarSign, Check, AlertTriangle } from 'lucide-react';
import { HelpTooltip } from './HelpTooltip';
import api from '../utils/api';

const FIELDS = [
  {
    key: 'offer_amount',
    label: 'Your offer / discount amount',
    placeholder: '50',
    example: 'e.g. 50 → "$50 Off" in your ads',
    help: 'The real dollar number for this campaign\u2019s special. Enter 50 and every ad or flyer that says "$X Off" becomes "$50 Off". Pick a number you can honor on every job.',
  },
  {
    key: 'monthly_payment',
    label: 'Financing — monthly payment',
    placeholder: '89',
    example: 'e.g. 89 → "as low as $89/month"',
    help: 'If you offer financing, this is the real monthly number homeowners see — "as low as $89/month". Get it from your financing provider. Don\u2019t offer financing? Leave it blank.',
  },
  {
    key: 'down_payment',
    label: 'Financing — down payment',
    placeholder: '0',
    example: 'e.g. 0 → "$0 down"',
    help: 'What the customer pays upfront with financing. "$0 down" is a strong hook — only promise it if your financing plan really allows it.',
  },
];

export const CampaignNumbersCard = ({ campaignId, onSaved }) => {
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState('');

  useEffect(() => {
    api.get(`/campaigns/${campaignId}/offers`)
      .then(({ data }) => { setData(data); setValues(data.values); })
      .catch(() => {});
  }, [campaignId]);

  if (!data || !data.any_needed) return null;

  const activeFields = FIELDS.filter((f) => data.needs[f.key]);
  const missing = activeFields.filter((f) => !String(values[f.key] || '').trim());

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/campaigns/${campaignId}/offers`, values);
      setFlash('Saved — your numbers are now in every ad and flyer below.');
      setTimeout(() => setFlash(''), 4000);
      onSaved && onSaved();
    } catch {
      setFlash('Save failed — try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-xl border p-6 mb-8 ${missing.length ? 'border-orange/50 bg-orange/5' : 'border-lime/40 bg-lime/5'}`} data-testid="campaign-numbers-card">
      <div className="flex items-center gap-2 mb-1">
        <DollarSign size={18} className={missing.length ? 'text-orange' : 'text-lime'} />
        <h3 className="text-white font-bold text-xl">Drop in your real numbers</h3>
        <HelpTooltip text="This campaign's ads use placeholder prices (shown as $X) until you enter your own. Type your real numbers once and they automatically fill into every ad, email, and flyer — nothing goes out saying '$X'." testId="help-numbers-card" />
      </div>
      {missing.length > 0 ? (
        <p className="text-sm text-orange flex items-center gap-1.5 mb-4" data-testid="numbers-missing-warning">
          <AlertTriangle size={14} className="shrink-0" /> Ads still say "$X" — enter your number → homeowners see a real offer.
        </p>
      ) : (
        <p className="text-sm text-lime flex items-center gap-1.5 mb-4" data-testid="numbers-complete">
          <Check size={14} className="shrink-0" /> Done — your real numbers are live in every asset below.
        </p>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        {activeFields.map((f) => (
          <div key={f.key}>
            <label className="flex items-center gap-1.5 text-sm text-light-gray font-semibold mb-1.5">
              {f.label}
              <HelpTooltip size={13} text={f.help} testId={`help-${f.key}`} />
            </label>
            <div className="flex items-center gap-2">
              <span className="text-lime font-bold">$</span>
              <input
                type="number"
                min="0"
                value={values[f.key] || ''}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                data-testid={`numbers-input-${f.key}`}
                className="w-full px-3 py-2.5 bg-black border border-divider rounded-lg text-white focus:outline-none focus:border-lime"
              />
            </div>
            <p className="text-xs text-gray mt-1">{f.example}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={save} disabled={saving} data-testid="numbers-save-btn"
          className="px-5 py-2.5 rounded-lg bg-lime text-black font-bold text-sm hover:bg-lime/90 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save my numbers'}
        </button>
        {flash && <span className="text-sm text-lime" data-testid="numbers-flash">{flash}</span>}
      </div>
    </div>
  );
};
